import React, { useState, useEffect } from 'react';
import { string, bool } from 'prop-types';
import { Button } from '@one-thd/sui-atomic-components';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { useDataModel } from '@thd-nucleus/data-sources';
import className from 'classnames';
import { LoadingPlaceholder } from '../core/LoadingPlaceholder';
import { CarouselHeader } from '../core/CarouselHeader';
import { SupplementalTilesCarousel } from './SupplementalTilesCarousel';
import { SupplementalTiles } from './SupplementalTiles';
import { SupplementalRecsPods } from './SupplementalRecsPods';
import { supplementalRecsdataModel } from '../dataModel';
import { publish } from '../utils/publisher';
import useCxtPropsAndOptions from '../hooks/useCxtPropsAndOptions';

const SupplementalRecsLoadingPlaceholder = () => {
  const { isMobile } = useCxtPropsAndOptions();

  return (
    <LoadingPlaceholder
      itemHeight={isMobile ? 44 : 56}
      itemsCount={6}
      txtPlaceholderLines={!isMobile ? 2 : 0}
    />
  );
};

const SupplementalRecsWrapper = (props) => {
  const { anchor, anchorId } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (mounted) {
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <SupplementalRecs {...props} anchor={anchor || anchorId} />
    );
  }

  return null;

};

const SupplementalRecs = ({
  anchorId,
  requestKey,
  schemaName,
  showLoading,
  title,
  buttonValue,
  subtitle,
  isDrawerShow,
  noATCFulfillment
}) => {
  const [viewDetails, setViewDetails] = useState(false);
  const { isMobile, opts } = useCxtPropsAndOptions(anchorId, requestKey, schemaName);

  const { data, error, loading } = useDataModel('recs', opts);

  const evntName = buttonValue || 'shop-the-look';
  const handleViewDetailsClick = () => {
    publish({
      componentName: 'supplemental-recs',
      eventName: 'shop-the-look-click',
      data: {
        eventName: evntName
      }
    });
    setViewDetails(true);
  };

  if (error || (!loading && !data?.recs)) {
    return null;
  }

  const wrapperClasses = className(
    'sui-w-full', 'sui-pt-0', 'sui-pb-0',
    'sui-p-4',
  );

  const productsData = data?.recs?.products;
  const containerTitle = title || data?.recs?.metadata?.title;
  const buttonText = buttonValue || 'Shop the Look';
  return (
    <div
      id={schemaName}
      data-type="container"
      data-component={`SupplementalRecs-${schemaName}`}
      className={wrapperClasses}
    >
      {
        showLoading && loading && !data?.recs
        && <SupplementalRecsLoadingPlaceholder />
      }
      {
        productsData?.length
        && (
          <>
            <CarouselHeader title={containerTitle} subtitle={subtitle} />
            <meta data-prop="name" content={schemaName} />
            <div className="sui-grid sui-grid-cols-1">
              {isMobile
                ? (
                  <SupplementalTiles
                    productsData={productsData}
                    schemaName={schemaName}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShow}
                  />
                )
                : (
                  <SupplementalTilesCarousel
                    productsData={productsData}
                    schemaName={schemaName}
                    onViewDetailsClick={() => setViewDetails(true)}
                    onClickShowDrawer={handleViewDetailsClick}
                    isDrawerShow={isDrawerShow}
                  />
                )}
            </div>
            <div className="sui-grid">
              <div className=" sui-mt-8 sm:sui-mt-1 sui-mb-2 sui-mr-auto sui-ml-auto sui-pb-8">
                <Button
                  variant="secondary"
                  onClick={handleViewDetailsClick}
                >
                  {buttonText}
                </Button>
              </div>
            </div>
            {viewDetails && (
              <SupplementalRecsPods
                productsData={productsData}
                storeId={opts.variables.storeId}
                zipCode={opts.variables.zipCode}
                schemaName={schemaName}
                isDrawerOpen={viewDetails}
                handleViewDetailsClick={() => setViewDetails(false)}
                podTitle={title}
                noATCFulfillment={noATCFulfillment}
              />
            )}
          </>
        )
      }
    </div>
  );
};

SupplementalRecsWrapper.propTypes = {
  anchor: string,
  anchorId: string.isRequired
};

SupplementalRecsWrapper.defaultProps = {
  anchor: null
};

SupplementalRecs.propTypes = {
  anchorId: string.isRequired,
  requestKey: string,
  schemaName: string.isRequired,
  showLoading: bool,
  title: string,
  buttonValue: string,
  subtitle: string,
  isDrawerShow: bool,
  noATCFulfillment: bool
};

SupplementalRecs.defaultProps = {
  showLoading: true,
  title: null,
  requestKey: null,
  buttonValue: null,
  subtitle: null,
  isDrawerShow: false,
  noATCFulfillment: true
};

SupplementalRecs.dataModel = supplementalRecsdataModel;
SupplementalRecs.displayName = 'SupplementalRecs';

const HydratedSupplementalRecs = withHydrator(
  {
    delay: 1500,
    placeholder: (<SupplementalRecsLoadingPlaceholder />),
    preserveCtxVal: 'clientStore'
  },
  SupplementalRecsWrapper
);

const SupplementalRecommendations = withErrorBoundary(HydratedSupplementalRecs);
SupplementalRecommendations.dataModel = supplementalRecsdataModel;

export { SupplementalRecommendations };
